import { v4 as uuidv4 } from 'uuid';
import { ComponentInput, ComponentType } from '@/types';

// Import compound chart templates
import compoundLineChartTemplate from '@/data/templates/molecules/compound-line-chart.json';
import compoundBarChartTemplate from '@/data/templates/molecules/compound-bar-chart.json';
import compoundAreaChartTemplate from '@/data/templates/molecules/compound-area-chart.json';

/**
 * Compound Chart Factory
 *
 * Creates compound chart layouts (wrapper + header + title + value + chart)
 * from templates, with unique IDs and proper linkedChartId references.
 */

interface CompoundChartOptions {
  /** Chart data to use instead of default */
  data?: Record<string, unknown>[];
  /** Remove template/default chart records when runtime binding will supply data */
  omitDefaultData?: boolean;
  /** Chart title text */
  title?: string;
  /** Field for X-axis */
  xAxisKey?: string;
  /** Fields for Y-axis (line/bar/area keys) */
  valueKeys?: string[];
  /** Parent container ID (if adding to existing container) */
  parentId?: string;
  /** Page ID */
  pageId?: string;
  /** Project ID */
  projectId?: string;
  /** Additional chart properties */
  chartProperties?: Record<string, unknown>;
}

interface CompoundChartResult {
  /** All generated components (wrapper, header, title, value-group, value, date, chart) */
  components: FactoryComponent[];
  /** The root wrapper component */
  wrapper: FactoryComponent;
  /** The chart component (for reference) */
  chart: FactoryComponent;
  /** The DataText value component (for reference) */
  valueDisplay: FactoryComponent | undefined;
  /** The DataText date component (for reference) */
  dateDisplay: FactoryComponent | undefined;
}

type ChartType = ComponentType.LINE_CHART | ComponentType.BAR_CHART | ComponentType.AREA_CHART;
type FactoryComponent = ComponentInput & {
  id: string;
  name: string;
  pageId: string;
  type: ComponentType;
  properties: Record<string, unknown>;
  children: string[];
  order: number;
};

type StyleSlots = {
  base?: Record<string, unknown>;
  mobile?: Record<string, unknown>;
  tablet?: Record<string, unknown>;
  desktop?: Record<string, unknown>;
};

// Template shape used for compound chart generation
interface CompoundChartTemplate {
  pages: Array<{
    components: FactoryComponent[];
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isStyleSlots(value: unknown): value is StyleSlots {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const componentTypeValues = new Set<string>(Object.values(ComponentType));

function isComponentType(value: unknown): value is ComponentType {
  return typeof value === 'string' && componentTypeValues.has(value);
}

function toFactoryComponent(value: unknown): FactoryComponent | null {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.name !== 'string' ||
    typeof value.pageId !== 'string' ||
    !isComponentType(value.type) ||
    (value.parentId !== undefined && typeof value.parentId !== 'string')
  ) {
    return null;
  }

  const properties = isRecord(value.properties) ? value.properties : {};
  const children =
    Array.isArray(value.children) && value.children.every((child) => typeof child === 'string')
      ? value.children
      : [];

  return {
    ...value,
    id: value.id,
    name: value.name,
    pageId: value.pageId,
    type: value.type,
    properties,
    children,
    order: typeof value.order === 'number' ? value.order : 0,
  };
}

function toCompoundChartTemplate(value: unknown, templateName: string): CompoundChartTemplate {
  if (!isRecord(value) || !Array.isArray(value.pages)) {
    throw new Error(`[compoundChartFactory] Invalid template "${templateName}"`);
  }

  const pages = value.pages.map((page, pageIndex) => {
    if (!isRecord(page) || !Array.isArray(page.components)) {
      throw new Error(`[compoundChartFactory] Invalid page ${pageIndex} in template "${templateName}"`);
    }

    const normalizedComponents = page.components
      .map(toFactoryComponent)
      .filter((component): component is FactoryComponent => component !== null);
    if (normalizedComponents.length !== page.components.length) {
      throw new Error(`[compoundChartFactory] Invalid page ${pageIndex} in template "${templateName}"`);
    }

    return {
      ...page,
      components: normalizedComponents,
    };
  });

  return {
    ...value,
    pages,
  };
}

// Map of chart types to their templates
const templateMap: Record<ChartType, CompoundChartTemplate> = {
  [ComponentType.LINE_CHART]: toCompoundChartTemplate(compoundLineChartTemplate, 'compound-line-chart'),
  [ComponentType.BAR_CHART]: toCompoundChartTemplate(compoundBarChartTemplate, 'compound-bar-chart'),
  [ComponentType.AREA_CHART]: toCompoundChartTemplate(compoundAreaChartTemplate, 'compound-area-chart'),
};

// Map of chart types to their value key property names
const valueKeyPropertyMap: Record<ChartType, string> = {
  [ComponentType.LINE_CHART]: 'lineKeys',
  [ComponentType.BAR_CHART]: 'barKeys',
  [ComponentType.AREA_CHART]: 'areaKeys',
};

const durableChartDataProperties = new Set<string>(['data', 'sampleData']);

function normalizeCompoundWrapperLayout(wrapper: FactoryComponent): void {
  const style = isStyleSlots(wrapper.style) ? wrapper.style : {};
  wrapper.style = {
    ...style,
    base: {
      ...(isStyleSlots(style.base) ? style.base : {}),
      width: '100%',
      backgroundColor: 'hsl(var(--card))',
      border: '1px solid hsl(var(--border) / 0.55)',
      boxShadow: 'none',
    },
  };

  wrapper.size = {
    ...(wrapper.size?.height !== undefined ? { height: wrapper.size.height } : {}),
    width: '100%',
  };

  wrapper.responsivePosition = {
    ...wrapper.responsivePosition,
    strategy: 'flex',
    constraints: {
      ...wrapper.responsivePosition?.constraints,
      base: {
        ...wrapper.responsivePosition?.constraints?.base,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '100%',
        alignSelf: 'stretch',
      },
      mobile: {
        ...wrapper.responsivePosition?.constraints?.mobile,
        flexBasis: '100%',
        alignSelf: 'stretch',
      },
      tablet: {
        ...wrapper.responsivePosition?.constraints?.tablet,
        flexBasis: '100%',
        alignSelf: 'stretch',
      },
      desktop: {
        ...wrapper.responsivePosition?.constraints?.desktop,
        flexBasis: '100%',
        alignSelf: 'stretch',
      },
    },
  };
}

function omitDurableChartDataProperties(
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const nextProperties: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties ?? {})) {
    if (!durableChartDataProperties.has(key)) {
      nextProperties[key] = value;
    }
  }

  return nextProperties;
}

function getUniqueValueKeys(valueKeys: string[] | undefined): string[] {
  return [...new Set((valueKeys ?? []).map((key) => key.trim()).filter(Boolean))];
}

function getPrimitiveDataValue(row: Record<string, unknown> | undefined, field: string): string | number | undefined {
  const value = row?.[field];
  return typeof value === 'string' || typeof value === 'number' ? value : undefined;
}

function getSeriesDisplayName(field: string): string {
  return field
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function createAdditionalValueDisplay(
  base: FactoryComponent,
  field: string,
  index: number,
  lastDataPoint: Record<string, unknown> | undefined,
): FactoryComponent {
  const value = getPrimitiveDataValue(lastDataPoint, field);

  return {
    ...base,
    id: uuidv4(),
    name: `${getSeriesDisplayName(field)} Value Display`,
    order: base.order + index,
    position: base.position
      ? {
          ...base.position,
          z: (base.position.z ?? 4) + index,
        }
      : base.position,
    properties: {
      ...base.properties,
      value: value ?? '--',
      valueField: field,
      hoverValueField: field,
    },
  };
}

function syncValueGroupChildren(
  components: FactoryComponent[],
  valueDisplay: FactoryComponent | undefined,
  dateDisplay: FactoryComponent | undefined,
  valueDisplayIds: string[],
): void {
  const parentId = valueDisplay?.parentId ?? dateDisplay?.parentId;
  if (!parentId) {
    return;
  }

  const parent = components.find((component) => component.id === parentId);
  if (!parent) {
    return;
  }

  const managedIds = new Set([
    ...valueDisplayIds,
    ...(dateDisplay ? [dateDisplay.id] : []),
  ]);
  const otherChildren = parent.children.filter((childId) => !managedIds.has(childId));

  parent.children = [
    ...valueDisplayIds,
    ...(dateDisplay ? [dateDisplay.id] : []),
    ...otherChildren,
  ];
}

/**
 * Creates a compound chart layout from a template.
 *
 * @param chartType - The type of chart (line, bar, or area)
 * @param options - Configuration options
 * @returns The generated components with unique IDs
 */
export function createCompoundChart(
  chartType: ChartType,
  options: CompoundChartOptions = {}
): CompoundChartResult {
  const template = templateMap[chartType];
  if (!template) {
    throw new Error(`No compound template found for chart type: ${chartType}`);
  }

  const templateComponents = template.pages[0]?.components;
  if (!templateComponents) {
    throw new Error('[compoundChartFactory] Template has no pages');
  }
  const templateWrapperComponent = templateComponents[0];
  if (!templateWrapperComponent) {
    throw new Error('[compoundChartFactory] Template has no components');
  }

  // Generate new unique IDs for all components
  const idMap = new Map<string, string>();
  templateComponents.forEach((comp) => {
    idMap.set(comp.id, uuidv4());
  });

  // Clone and update components with new IDs
  const components: FactoryComponent[] = templateComponents.map((templateComp) => {
    const newId = idMap.get(templateComp.id);
    if (newId === undefined) {
      throw new Error(
        `[compoundChartFactory] missing idMap entry for id "${templateComp.id}"`,
      );
    }

    const comp: FactoryComponent = {
      ...templateComp,
      id: newId,
      ...(options.pageId && { pageId: options.pageId }),
    };

    // Update parentId if it exists
    if (templateComp.parentId && idMap.has(templateComp.parentId)) {
      const newParentId = idMap.get(templateComp.parentId);
      if (newParentId !== undefined) {
        comp.parentId = newParentId;
      }
    } else if (options.parentId && !templateComp.parentId) {
      // If adding to a parent container and this is a root component
      // Note: We only set parentId on the wrapper, not all components
    }

    // Update children array with new IDs
    if (templateComp.children && Array.isArray(templateComp.children)) {
      comp.children = templateComp.children.map((childId: string) =>
        idMap.get(childId) ?? childId
      );
    }

    // Update linkedChartId for DataText components
    if (
      comp.type === ComponentType.DATA_TEXT &&
      typeof comp.properties?.linkedChartId === 'string'
    ) {
      const linkedId = comp.properties.linkedChartId;
      if (idMap.has(linkedId)) {
        comp.properties = {
          ...comp.properties,
          linkedChartId: idMap.get(linkedId) ?? linkedId,
        };
      }
    }

    return comp;
  });

  // Find the key components
  const wrapper = components.find(c => c.id === idMap.get(templateWrapperComponent.id));
  if (!wrapper) {
    throw new Error('[compoundChartFactory] Template has no wrapper component');
  }
  normalizeCompoundWrapperLayout(wrapper);

  const chart = components.find(c =>
    c.type === chartType ||
    c.type === ComponentType.LINE_CHART ||
    c.type === ComponentType.BAR_CHART ||
    c.type === ComponentType.AREA_CHART
  );
  if (!chart) {
    throw new Error('[compoundChartFactory] Template has no chart component');
  }

  if (options.omitDefaultData) {
    chart.properties = omitDurableChartDataProperties(chart.properties);
  }

  // Find DataText components - value display is the one with valueField='value', date is valueField='name'
  const dataTextComponents = components.filter(c => c.type === ComponentType.DATA_TEXT);
  const valueDisplay: FactoryComponent | undefined = dataTextComponents.find(c =>
    c.properties?.valueField === 'value' || c.name?.includes('Live Value')
  ) ?? dataTextComponents[0];
  const dateDisplay: FactoryComponent | undefined = dataTextComponents.find(c =>
    c.properties?.valueField === 'name' || c.name?.includes('Date Display')
  ) ?? dataTextComponents[1];
  const titleComp = components.find(c =>
    c.type === ComponentType.TEXT && c.name?.includes('Title')
  );

  // Apply custom options

  // Update chart title
  if (options.title && titleComp) {
    titleComp.properties = {
      ...titleComp.properties,
      content: options.title,
    };
  }

  // Update chart data
  if (options.data !== undefined && chart) {
    chart.properties = {
      ...chart.properties,
      data: options.data,
    };

    // Also update the DataText value if we have data
    // Show the last value (current value) as default
    if (valueDisplay && options.data.length > 0) {
      const valueFieldFromChartProperties =
        typeof options.chartProperties?.valueField === 'string'
          ? options.chartProperties.valueField
          : undefined;
      const valueField = valueFieldFromChartProperties
        ?? options.valueKeys?.[0]
        ?? 'value';
      const lastDataPoint = options.data[options.data.length - 1];
      const lastValue = lastDataPoint[valueField];
      if (lastValue !== undefined) {
        valueDisplay.properties = {
          ...valueDisplay.properties,
          value: lastValue,
          valueField,
        };
      }
    }
  }

  // Update xAxisKey
  if (options.xAxisKey && chart) {
    chart.properties = {
      ...chart.properties,
      xAxisKey: options.xAxisKey,
    };
  }

  // Update value keys (lineKeys, barKeys, areaKeys)
  if (options.valueKeys && chart) {
    const valueKeys = getUniqueValueKeys(options.valueKeys);
    const valueKeyProp = valueKeyPropertyMap[chartType];
    chart.properties = {
      ...chart.properties,
      [valueKeyProp]: valueKeys,
    };

    if (valueDisplay && valueKeys[0]) {
      const lastDataPoint = options.data?.[options.data.length - 1];
      const valueDisplayIds = [valueDisplay.id];
      const firstValue = getPrimitiveDataValue(lastDataPoint, valueKeys[0]);

      valueDisplay.properties = {
        ...valueDisplay.properties,
        value: firstValue ?? valueDisplay.properties.value ?? '--',
        valueField: valueKeys[0],
        hoverValueField: valueKeys[0],
      };

      for (let index = 1; index < valueKeys.length; index += 1) {
        const field = valueKeys[index];
        const display = createAdditionalValueDisplay(valueDisplay, field, index, lastDataPoint);
        valueDisplayIds.push(display.id);
        components.push(display);
      }

      syncValueGroupChildren(components, valueDisplay, dateDisplay, valueDisplayIds);
    }
  }

  // Update date DataText to use the xAxisKey
  if (options.xAxisKey && dateDisplay) {
    dateDisplay.properties = {
      ...dateDisplay.properties,
      valueField: options.xAxisKey,
    };
  }

  // Apply additional chart properties
  if (options.chartProperties && chart) {
    chart.properties = {
      ...chart.properties,
      ...options.chartProperties,
    };
  }

  // If adding to an existing parent, update the wrapper's parentId
  if (options.parentId) {
    wrapper.parentId = options.parentId;
  }

  return {
    components,
    wrapper,
    chart,
    valueDisplay,
    dateDisplay,
  };
}

/**
 * Check if a component type supports compound chart layouts.
 */
export function isCompoundChartType(type: ComponentType): type is ChartType {
  return (
    type === ComponentType.LINE_CHART ||
    type === ComponentType.BAR_CHART ||
    type === ComponentType.AREA_CHART
  );
}

/**
 * Get the display name for a chart type.
 */
export function getChartDisplayName(type: ChartType): string {
  switch (type) {
    case ComponentType.LINE_CHART:
      return 'Line Chart';
    case ComponentType.BAR_CHART:
      return 'Bar Chart';
    case ComponentType.AREA_CHART:
      return 'Area Chart';
    default:
      return 'Chart';
  }
}
